import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import logo_eagle from "./assets/img/logo_eagle.png";
import Select from "react-select";
import {
    FaBuilding, FaBriefcase, FaLayerGroup, FaUserTag,
    FaSearch, FaPlus, FaPenSquare, FaTrashAlt,
} from "react-icons/fa";
import { apiFetch } from "./utils/api";
import "./dSpr.css";
import "./Employee_configuration.css";

function EmployeeConfiguration({ navSize }) {
    const [activeTab, setActiveTab] = useState("department");
    const [data, setData] = useState([]);
    const [newName, setNewName] = useState("");
    const [filterValue, setFilterValue] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc"); // A-Z by default
    const [employees, setEmployees] = useState([]);
    const navigate = useNavigate();

    const sortOptions = [
        { value: "asc", label: "A-Z" },
        { value: "desc", label: "Z-A" },
    ];

    useEffect(() => {
        fetchloginedEmployees();
        fetchData();
    }, [activeTab]);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            // Role redirect removed - ProtectedRoute (App.js) gates via matrix.

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    // 🔹 Fetch all records
    const fetchData = async () => {
        try {
            const res = await apiFetch(`/api/Employee_configuration/get-${current.api}`);
            const json = await res.json();
            setData(json);
        } catch (err) {
            console.error("❌ Fetch error:", err);
        }
    };

    // 🔹 Add record
    const handleAdd = async () => {
        if (!newName.trim()) {
            dnAlert.error("Error", "Name cannot be empty");
            return;
        }

        try {
            const res = await apiFetch(`/api/Employee_configuration/add-${current.api}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: newName }),
                credentials: 'include',
            });

            let data;
            try {
                data = await res.json();
            } catch {
                throw { message: "Server Error: Invalid response format" };
            }

            if (data.success) {
                dnAlert.success("Success", "Added successfully");
                setNewName("");
                fetchData();
            } else {
                throw data;
            }

        } catch (error) {
            console.error("❌ Add Error:", error);
            const serverError =
                error.sqlMessage ||
                error.reason ||
                error.error ||
                error.message ||
                "Unknown Database Error";

            dnAlert.error("Add Failed", serverError);
        }
    };

    // 🔹 Update record
    const handleUpdate = async (id, oldName) => {
        const name = await dnAlert.prompt({
            title: "Edit Name",
            defaultValue: oldName,
        });

        if (name && name !== oldName) {
            try {
                const res = await apiFetch(`/api/Employee_configuration/update-${current.api}/${id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name }),
                    credentials: 'include',
                });

                let data;
                try {
                    data = await res.json();
                } catch {
                    throw { message: "Server Error: Invalid response format" };
                }

                if (data.success) {
                    dnAlert.success("Updated", "Record updated successfully");
                    fetchData();
                } else {
                    throw data;
                }

            } catch (error) {
                console.error("❌ Update Error:", error);
                const serverError =
                    error.sqlMessage ||
                    error.reason ||
                    error.error ||
                    error.message ||
                    "Unknown Database Error";

                dnAlert.error("Update Failed", serverError);
            }
        }
    };

    // 🔹 Delete record
    const handleDelete = async (id) => {
        const ok = await dnAlert.confirm({
            title: "Delete?",
            message: "Are you sure you want to delete this record?",
            danger: true,
            confirmLabel: "Delete",
        });
        if (!ok) return;

        try {
            const res = await apiFetch(`/api/Employee_configuration/delete-${current.api}/${id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                credentials: 'include',
            });

            let data;
            try {
                data = await res.json();
            } catch {
                throw { message: "Server Error: Invalid response format" };
            }

            if (data.success) {
                dnAlert.success("Deleted", "Record deleted successfully");
                fetchData();
            } else {
                throw data;
            }

        } catch (error) {
            console.error("❌ Delete Error:", error);
            const serverError =
                error.sqlMessage ||
                error.reason ||
                error.error ||
                error.message ||
                "Unknown Database Error";

            dnAlert.error("Delete Failed", serverError);
        }
    };

    const configTables = {
        department: { title: "Departments",     api: "department_config",     nameField: "department_name", idField: "department_id", icon: FaBuilding },
        job:        { title: "Job Positions",   api: "job_position_config",   nameField: "job_name",        idField: "job_id",        icon: FaBriefcase },
        level:      { title: "Career Levels",   api: "career_level_config",   nameField: "level_name",      idField: "level_id",      icon: FaLayerGroup },
        type:       { title: "Employee Types",  api: "employee_type_config",  nameField: "type_name",       idField: "type_id",       icon: FaUserTag },
    };

    const current = configTables[activeTab]; // ✅ define first

    // 🔹 Filter logic AFTER current is defined
    const filteredData = filterValue
        ? data.filter((item) =>
            item[current.nameField]
                .toLowerCase()
                .includes(filterValue.toLowerCase())
        )
        : data;

    // 🔹 Apply user-selected A-Z / Z-A reorder
    const sortedData = [...filteredData].sort((a, b) => {
        const av = (a[current.nameField] || "").toString().toLowerCase();
        const bv = (b[current.nameField] || "").toString().toLowerCase();
        return sortOrder === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
    });

    const singular = current.title.endsWith("s") ? current.title.slice(0, -1) : current.title;

    return (
        <div className="emp-config-container">
            <div className={`emp-config-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img
                                src={logo_eagle}
                                alt="dAdmin Logo"
                                style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }}
                            />
                        </a>
                        Employee Configuration
                    </h2>
                    <p className="dspr-subtitle">
                        Maintain the master lists that drive Employee Center dropdowns - departments,
                        job positions, career levels, and employee types.
                    </p>
                </div>

                <div className="emp-config-content">
                    {/* ── Tab bar (one per master list) ───────────────────── */}
                    <div className="ec-tabs">
                        {Object.keys(configTables).map((key) => {
                            const t = configTables[key];
                            const Icon = t.icon;
                            return (
                                <button
                                    key={key}
                                    className={`ec-tab ${activeTab === key ? "active" : ""}`}
                                    onClick={() => {
                                        setFilterValue(null);
                                        setActiveTab(key);
                                    }}
                                >
                                    <Icon /> {t.title}
                                </button>
                            );
                        })}
                    </div>

                    {/* ── Card surface ───────────────────────────────────── */}
                    <section className="ec-card">
                        <div className="ec-card-header">
                            <div className="ec-card-title">
                                <current.icon />
                                <span>{current.title}</span>
                                <span className="ec-card-count">{sortedData.length}</span>
                            </div>
                        </div>

                        {/* ── Toolbar: search + sort + add ──────────────── */}
                        <div className="ec-toolbar">
                            <div className="ec-search">
                                <FaSearch />
                                <Select
                                    placeholder={`Search ${current.title.toLowerCase()}…`}
                                    options={data.map((item) => ({
                                        value: item[current.nameField],
                                        label: item[current.nameField],
                                    }))}
                                    value={filterValue ? { value: filterValue, label: filterValue } : null}
                                    onChange={(selected) => setFilterValue(selected ? selected.value : null)}
                                    isClearable
                                    isSearchable
                                    classNamePrefix="ec-select"
                                    styles={ecSelectStyles}
                                />
                            </div>

                            <Select
                                placeholder="Sort"
                                options={sortOptions}
                                value={sortOptions.find(o => o.value === sortOrder)}
                                onChange={(selected) => setSortOrder(selected ? selected.value : "asc")}
                                isSearchable={false}
                                classNamePrefix="ec-select"
                                styles={{ ...ecSelectStyles, container: (p) => ({ ...p, width: 130 }) }}
                            />

                            <div className="ec-add">
                                <input
                                    type="text"
                                    className="ec-input"
                                    placeholder={`Add new ${singular.toLowerCase()}`}
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
                                />
                                <button className="ec-add-btn" onClick={handleAdd}>
                                    <FaPlus /> Add
                                </button>
                            </div>
                        </div>

                        {/* ── Table ──────────────────────────────────────── */}
                        {sortedData.length === 0 ? (
                            <div className="ec-empty">
                                {data.length === 0
                                    ? `No ${current.title.toLowerCase()} yet. Add one using the form above.`
                                    : `No ${current.title.toLowerCase()} match "${filterValue}".`}
                            </div>
                        ) : (
                            <div className="ec-table-wrap">
                                <table className="ec-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 70 }}>S.NO</th>
                                            <th>{singular.toUpperCase()} NAME</th>
                                            <th style={{ width: 140 }}>ACTIONS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedData.map((item, idx) => (
                                            <tr key={item[current.idField]}>
                                                <td className="ec-cell-sno">{idx + 1}</td>
                                                <td>{item[current.nameField]}</td>
                                                <td className="ec-cell-actions">
                                                    <button
                                                        className="ec-icon-btn"
                                                        title="Edit"
                                                        onClick={() => handleUpdate(item[current.idField], item[current.nameField])}
                                                    >
                                                        <FaPenSquare />
                                                    </button>
                                                    <button
                                                        className="ec-icon-btn danger"
                                                        title="Delete"
                                                        onClick={() => handleDelete(item[current.idField])}
                                                    >
                                                        <FaTrashAlt />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}

// Shared react-select styling for the toolbar pickers - matches the
// app's input look (rounded, light border, navy focus).
const ecSelectStyles = {
    container: (p) => ({ ...p, width: "100%" }),
    control: (p, s) => ({
        ...p,
        borderRadius: 10,
        minHeight: 42,
        borderColor: s.isFocused ? "#fb923c" : "#e2e8f0",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(251,146,60,0.15)" : "none",
        "&:hover": { borderColor: "#fb923c" },
    }),
    option: (p, s) => ({
        ...p,
        // isSelected before isFocused so the selected option keeps its
        // navy bg even when react-select auto-focuses it on open.
        backgroundColor: s.isSelected ? "#0d1b2a" : s.isFocused ? "#f1f5f9" : "white",
        color: s.isSelected ? "white" : "#0d1b2a",
        padding: "10px 12px",
    }),
};

export default EmployeeConfiguration;
